/**
 * Стор пользователя — загрузка и управление данными текущего пользователя
 *
 * Основные функции:
 * - Загрузка данных пользователя через GetCurrentUser
 * - Обновление email через UpdateCurrentUser (для привязки email через magic link)
 */

// Фабрика Pinia сторов
import { defineStore } from 'pinia'
// Обёртка gRPC-вызовов с обработкой токенов и ошибок
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
// Фабрика gRPC-клиентов для всех эндпоинтов
import { $grpcClient } from '@/grpc-client/grpc-client'
// Логгер событий с отправкой ошибок на бэкенд
import { EventLogger } from '@/utils/EventLogger'
// Инъекция accessToken в gRPC-запросы
import { grpcReq } from '@/grpc-client/grpc/grpc-request'
// Стор обратной связи — глобальный лоадер и тост-уведомления
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Утилита конвертации proto Timestamp в миллисекунды
import { protoTimestampToMs } from '@/utils/dateFormatters'
// Текст ошибки: бэкенд → иначе заглушка
import { grpcErrorMessage } from '@/utils/grpcErrorMessage'
import { tGlobal } from '@/plugins/i18n'
// Типизация состояния стора
import type { IUserStoreState } from '@/modules/user/types/IUserStoreState'

export const useUserStore = defineStore('userStore', {
  state: (): IUserStoreState => ({
    isUserData: false,
    user: {
      id: '',
      isAdmin: false,
      telegramID: '',
      email: '',
      firstName: '',
      lastName: '',
      avatarURL: '',
      referralLink: '',
      isTariffActive: false,
      referralCount: 0,
      referralBonusDaysCount: 0,
      lastTariffExpiresAt: null,
      nextChargeAt: null,
      isOnboardingCompleted: false,
      hasUsedPromo: false,
      isShowNamePublic: false,
    },
    leaderboard: [],
    currentUserIndex: -1,
    totalReferrals: 0,
  }),
  actions: {
    /**
     * Загружает данные текущего пользователя с сервера
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getUserData(): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      try {
        const [ok, response] = await grpcCall(
          (_args, options) => $grpcClient.UserEndpoint.getCurrentUser(grpcReq({}), options),
          'getUserData',
        )
        if (ok && response.user) {
          if (!this.user) return false



          // Маппинг данных пользователя из gRPC-ответа
          this.user.id = response.user.id
          this.user.telegramID = response.user.telegramID
          this.user.email = response.user.email ?? ''
          this.user.firstName = response.user.firstName ?? ''
          this.user.lastName = response.user.lastName ?? ''
          this.user.avatarURL = response.user.avatarURL ?? ''
          this.user.referralLink = response.user.referralLink
          this.user.isTariffActive = response.user.isTariffActive
          this.user.referralCount = response.user.referralCount
          this.user.referralBonusDaysCount = response.user.referralBonusDaysCount
          this.user.lastTariffExpiresAt = response.user.lastTariffExpiresAt
            ? protoTimestampToMs(response.user.lastTariffExpiresAt)
            : null
          this.user.lastTariffID = response.user.lastTariffID
          this.user.nextChargeAt = response.user.nextChargeAt
            ? protoTimestampToMs(response.user.nextChargeAt)
            : null
          this.user.isOnboardingCompleted = response.user.isOnboardingCompleted ?? false
          this.user.hasUsedPromo = response.user.hasUsedPromo ?? false
          this.user.isShowNamePublic = response.user.isShowNamePublic ?? false
          this.isUserData = true
          return true
        }
        if (response && response.error) {
          EventLogger.error('getUserData', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.loadUserFailed'),
          })
        } else {
          EventLogger.error('getUserData', ['Не удалось выполнить загрузку данных через gRPC'])
        }
        return false
      } catch (error: unknown) {
        console.error('getUserData CATCH:', error)
        EventLogger.error('getUserData', [`Ошибка getUserData: ${(error as Error).message}`])
        return false
      }
    },

    /**
     * Обновляет email текущего пользователя через UpdateCurrentUser
     * Записывает newEmail — бэкенд автоматически отправляет письмо с кодом подтверждения
     *
     * @param {string} email - Новый email для привязки
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат обновления
     */
    async updateUserEmail(email: string): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                newEmail: email,
                clearTelegram: false,
                clearEmail: false,
              }),
              options,
            ),
          'updateUserEmail',
        )

        if (ok) {
          return { ok: true }
        }

        if (response.error) {
          EventLogger.error('updateUserEmail', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return { ok: false, error: grpcErrorMessage(response.error, 'errors.updateEmailFailed') }
        }

        EventLogger.error('updateUserEmail', ['Не удалось обновить email'])
        return { ok: false, error: tGlobal('errors.updateEmailFailed') }
      } catch (error: unknown) {
        EventLogger.error('updateUserEmail', [
          `Ошибка updateUserEmail: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.updateEmailFailed') }
      }
    },

    /**
     * Помечает онбординг как завершенный на бэкенде через UpdateCurrentUser
     *
     * @returns {Promise<boolean>} Успешность операции
     */
    async completeOnboarding(): Promise<{ ok: boolean; error?: string }> {
      const fallbackKey = 'errors.onboardingFailed'
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                isOnboardingCompleted: true,
                newEmail: '',
                clearTelegram: false,
                clearEmail: false,
              }),
              options,
            ),
          'completeOnboarding',
        )

        if (ok) {
          if (this.user) {
            this.user.isOnboardingCompleted = true
          }
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('completeOnboarding', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
        }
        return { ok: false, error: grpcErrorMessage(response?.error, fallbackKey) }
      } catch (error: unknown) {
        EventLogger.error('completeOnboarding', [
          `Ошибка completeOnboarding: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal(fallbackKey) }
      }
    },

    /**
     * Удаляет аккаунт текущего пользователя через DeleteCurrentUser
     *
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат удаления
     */
    async deleteCurrentUser(): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.deleteCurrentUser(
              grpcReq({}),
              options,
            ),
          'deleteCurrentUser',
        )

        if (ok) {
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('deleteCurrentUser', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return {
            ok: false,
            error: grpcErrorMessage(response.error, 'errors.deleteAccountFailed'),
          }
        }

        EventLogger.error('deleteCurrentUser', ['Не удалось удалить аккаунт'])
        return { ok: false, error: tGlobal('errors.deleteAccountFailed') }
      } catch (error: unknown) {
        EventLogger.error('deleteCurrentUser', [
          `Ошибка deleteCurrentUser: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.deleteAccountFailed') }
      }
    },

    /**
     * Отвязывает Telegram текущего пользователя через UpdateCurrentUser
     *
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат отвязки
     */
    async clearTelegram(): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                clearTelegram: true,
                clearEmail: false,
                newEmail: '',
              }),
              options,
            ),
          'clearTelegram',
        )

        if (ok) {
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('clearTelegram', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return {
            ok: false,
            error: grpcErrorMessage(response.error, 'errors.unlinkTelegramFailed'),
          }
        }

        EventLogger.error('clearTelegram', ['Не удалось отвязать Telegram'])
        return { ok: false, error: tGlobal('errors.unlinkTelegramFailed') }
      } catch (error: unknown) {
        EventLogger.error('clearTelegram', [
          `Ошибка clearTelegram: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.unlinkTelegramFailed') }
      }
    },

    /**
     * Отвязывает Email текущего пользователя через UpdateCurrentUser
     *
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат отвязки
     */
    async clearEmail(): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                clearTelegram: false,
                clearEmail: true,
                newEmail: '',
              }),
              options,
            ),
          'clearEmail',
        )

        if (ok) {
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('clearEmail', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return {
            ok: false,
            error: grpcErrorMessage(response.error, 'errors.unlinkEmailFailed'),
          }
        }

        EventLogger.error('clearEmail', ['Не удалось отвязать Email'])
        return { ok: false, error: tGlobal('errors.unlinkEmailFailed') }
      } catch (error: unknown) {
        EventLogger.error('clearEmail', [
          `Ошибка clearEmail: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.unlinkEmailFailed') }
      }
    },

    /**
     * Загружает таблицу лидеров рефералов с бэкенда
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getReferralLeaderboard(): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.getReferralLeaderboard(
              grpcReq({ userID: this.user?.id || '' }),
              options
            ),
          'getReferralLeaderboard',
        )
        if (ok && response) {
          this.leaderboard = response.entries || []
          this.currentUserIndex = response.currentUserIndex ?? -1
          this.totalReferrals = response.totalReferrals ?? 0
          return true
        }
        if (response?.error) {
          EventLogger.error('getReferralLeaderboard', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.leaderboardFailed'),
          })
        }
        return false
      } catch (error: unknown) {
        console.error('getReferralLeaderboard CATCH:', error)
        EventLogger.error('getReferralLeaderboard', [
          `Ошибка getReferralLeaderboard: ${(error as Error).message}`,
        ])
        return false
      }
    },

    /**
     * Обновляет имя текущего пользователя
     *
     * @param {string} firstName - Новое имя
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат обновления
     */
    async updateProfileName(firstName: string): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                firstName,
                newEmail: '',
                clearTelegram: false,
                clearEmail: false,
              }),
              options,
            ),
          'updateProfileName',
        )

        if (ok) {
          if (this.user) {
            this.user.firstName = firstName
          }
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('updateProfileName', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return {
            ok: false,
            error: grpcErrorMessage(response.error, 'errors.updateNameFailed'),
          }
        }
        return { ok: false, error: tGlobal('errors.updateNameFailed') }
      } catch (error: unknown) {
        EventLogger.error('updateProfileName', [
          `Ошибка updateProfileName: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.updateNameFailed') }
      }
    },

    /**
     * Обновляет флаг отображения имени в рейтинге
     *
     * @param {boolean} isShowNamePublic - Флаг публичности имени
     * @returns {Promise<{ ok: boolean; error?: string }>} Результат обновления
     */
    async updateShowNamePublic(isShowNamePublic: boolean): Promise<{ ok: boolean; error?: string }> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.updateCurrentUser(
              grpcReq({
                isShowNamePublic,
                newEmail: '',
                clearTelegram: false,
                clearEmail: false,
              }),
              options,
            ),
          'updateShowNamePublic',
        )

        if (ok) {
          if (this.user) {
            this.user.isShowNamePublic = isShowNamePublic
          }
          return { ok: true }
        }

        if (response?.error) {
          EventLogger.error('updateShowNamePublic', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          return {
            ok: false,
            error: grpcErrorMessage(response.error, 'errors.privacyFailed'),
          }
        }
        return { ok: false, error: tGlobal('errors.privacyFailed') }
      } catch (error: unknown) {
        EventLogger.error('updateShowNamePublic', [
          `Ошибка updateShowNamePublic: ${(error as Error).message}`,
        ])
        return { ok: false, error: tGlobal('errors.privacyFailed') }
      }
    },
  },
})
